import React from 'react';
import { X } from 'lucide-react';
import './RichMediaAnnouncementModal.css';

const RichMediaAnnouncementModal = ({ announcement, onClose, onCtaClick }) => {
  if (!announcement) return null;

  const { 
    title, 
    message, 
    icon,
    media_type,
    media_url,
    media_thumbnail,
    media_orientation = 'landscape',
    modal_layout = 'hero',
    cta_text,
    cta_link,
    secondary_cta_text,
    secondary_cta_link,
    theme,
    background_color,
    text_color
  } = announcement;

  const handleCtaClick = (link, isPrimary = true) => {
    if (onCtaClick) {
      onCtaClick(announcement.id, isPrimary);
    }
    if (link) {
      window.open(link, '_blank');
    }
  };

  const renderMedia = () => {
    const orientationClass = `media-orientation-${media_orientation}`;
    
    if (media_type === 'image' && media_url) {
      return (
        <img 
          src={media_url} 
          alt={title}
          className={`announcement-media-image ${orientationClass}`}
        />
      );
    }
    if (media_type === 'video' && media_url) {
      return (
        <video 
          src={media_url}
          poster={media_thumbnail}
          controls
          autoPlay
          muted
          loop
          className={`announcement-media-video ${orientationClass}`}
        >
          Your browser does not support video playback.
        </video>
      );
    }
    return null;
  };

  const renderCTAs = () => (
    <div className="announcement-cta-group">
      {cta_text && (
        <button
          onClick={() => handleCtaClick(cta_link, true)}
          className="announcement-cta-primary"
        >
          {cta_text}
        </button>
      )}
      {secondary_cta_text && (
        <button
          onClick={() => handleCtaClick(secondary_cta_link, false)}
          className="announcement-cta-secondary"
        >
          {secondary_cta_text}
        </button>
      )}
    </div>
  );

  // HERO LAYOUT - Full width image/video with overlay text
  const renderHeroLayout = () => {
    const hasMedia = media_type && media_type !== 'none' && media_url;
    
    return (
      <div 
        className="announcement-layout announcement-layout-hero"
        style={{
          ...(hasMedia && media_type === 'image' ? {
            backgroundImage: `url(${media_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {})
        }}
      >
        <div className="announcement-hero-overlay">
          <div className="announcement-hero-content">
            {icon && <span className="announcement-icon-large">{icon}</span>}
            <h2 className="announcement-title-hero">{title}</h2>
            <p className="announcement-message-hero">{message}</p>
            {renderCTAs()}
          </div>
        </div>
      </div>
    );
  };

  // SPLIT LAYOUT - Image/Video on left, content on right
  const renderSplitLayout = (imageOnLeft = true) => (
    <div className={`announcement-layout announcement-layout-split ${imageOnLeft ? 'image-left' : 'image-right'}`}>
      <div className="announcement-split-media">
        {renderMedia()}
      </div>
      <div className="announcement-split-content">
        {icon && <span className="announcement-icon-medium">{icon}</span>}
        <h2 className="announcement-title-split">{title}</h2>
        <p className="announcement-message-split">{message}</p>
        {renderCTAs()}
      </div>
    </div>
  );

  // CARD LAYOUT - Compact card style with image on top
  const renderCardLayout = () => (
    <div className="announcement-layout announcement-layout-card">
      {media_url && (
        <div className="announcement-card-media">
          {renderMedia()}
        </div>
      )}
      <div className="announcement-card-content">
        {icon && <span className="announcement-icon-medium">{icon}</span>}
        <h2 className="announcement-title-card">{title}</h2>
        <p className="announcement-message-card">{message}</p>
        {renderCTAs()}
      </div>
    </div>
  );

  // VIDEO BACKGROUND LAYOUT - Video playing in background
  const renderVideoBgLayout = () => (
    <div className="announcement-layout announcement-layout-video-bg">
      {media_type === 'video' && media_url && (
        <video 
          src={media_url}
          autoPlay
          muted
          loop
          playsInline
          className="announcement-video-bg"
        />
      )}
      <div className="announcement-video-bg-overlay">
        <div className="announcement-video-bg-content">
          {icon && <span className="announcement-icon-large">{icon}</span>}
          <h2 className="announcement-title-video-bg">{title}</h2>
          <p className="announcement-message-video-bg">{message}</p>
          {renderCTAs()}
        </div>
      </div>
    </div>
  );

  const renderLayout = () => {
    switch (modal_layout) {
      case 'hero':
        return renderHeroLayout();
      case 'split':
      case 'image_right':
        return renderSplitLayout(false);
      case 'image_left':
        return renderSplitLayout(true);
      case 'card':
        return renderCardLayout();
      case 'video_bg':
        return renderVideoBgLayout();
      default:
        return renderHeroLayout();
    }
  };

  return (
    <div className="announcement-modal-overlay" onClick={onClose}>
      <div 
        className={`announcement-modal-container theme-${theme}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          ...(theme === 'custom' && background_color ? { '--announcement-bg': background_color } : {}),
          ...(theme === 'custom' && text_color ? { '--announcement-text': text_color } : {})
        }}
      >
        <button 
          className="announcement-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={24} />
        </button>
        {renderLayout()}
      </div>
    </div>
  );
};

export default RichMediaAnnouncementModal;
